import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet,
  Plus,
  X,
  RefreshCw,
  CheckCircle2,
  Clock,
  Trash2,
  User,
  IndianRupee,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Save,
  AlertCircle,
  TrendingUp,
  Gift,
  Loader2,
  Eye,
  ChevronDown,
  Minus,
  BadgeCheck,
  BanknoteIcon,
  UserCog,
  Users,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ───────────────────────────────────────────────────
const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(n || 0);

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const prevMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const nextMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(y, m - 1, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const ROLE_META = {
  admin: {
    color: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    dot: "bg-amber-400",
  },
  manager: {
    color: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    dot: "bg-blue-400",
  },
  billing: {
    color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    dot: "bg-emerald-400",
  },
  staff: {
    color: "bg-stone-500/20 text-stone-300 border-stone-500/30",
    dot: "bg-stone-400",
  },
};
const roleMeta = (r) => ROLE_META[r] || ROLE_META.staff;

const PAYMENT_MODES = ["cash", "bank_transfer", "upi", "cheque"];

// ── Set Salary Modal ──────────────────────────────────────────
const SetSalaryModal = ({ staff, existingSalary, onClose, onSave }) => {
  const [salary, setSalary] = useState(existingSalary ?? "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const val = parseFloat(salary);
    if (isNaN(val) || val < 0) {
      toast.error("Enter a valid salary amount");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        user_id: staff.user_id || staff.id,
        base_salary: val,
        notes,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const displayName = staff.user_name || staff.name;
  const displayRole = staff.user_role || staff.role;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-xl border border-amber-500/20">
              <UserCog size={17} className="text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                Set Base Salary
              </p>
              <p className="text-white font-black text-sm">{displayName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${roleMeta(displayRole).color}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${roleMeta(displayRole).dot}`}
            />
            {displayRole}
          </div>

          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-2 font-bold">
              Monthly Base Salary (₹)
            </p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400 font-black text-lg">
                ₹
              </span>
              <input
                type="number"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                placeholder="0.00"
                min="0"
                step="100"
                autoFocus
                className="w-full h-14 bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 text-white text-2xl font-black font-mono outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-700"
              />
            </div>
          </div>

          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
              Notes (optional)
            </p>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Revised from April 2025"
              className="w-full h-10 bg-white/5 border border-white/10 rounded-xl px-4 text-stone-300 text-sm outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Save size={15} />
            )}
            Save Salary
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Pay Salary Modal ──────────────────────────────────────────
const PaySalaryModal = ({ staffEntry, month, onClose, onPaid, token }) => {
  const [mode, setMode] = useState("cash");
  const [bonus, setBonus] = useState(staffEntry.bonus || 0);
  const [deductions, setDeductions] = useState(staffEntry.deductions || 0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const net = useMemo(
    () =>
      Math.max(
        0,
        staffEntry.base_salary +
          staffEntry.commission_total +
          parseFloat(bonus || 0) -
          parseFloat(deductions || 0),
      ),
    [staffEntry.base_salary, staffEntry.commission_total, bonus, deductions],
  );

  const handlePay = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        month,
        payment_mode: mode,
        notes,
        deductions: String(parseFloat(deductions) || 0),
        bonus: String(parseFloat(bonus) || 0),
      });
      const { data } = await axios.post(
        `${API_URL}/salaries/pay/${staffEntry.user_id}?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onPaid(data);
      toast.success(
        `Salary of ₹${fmtMoney(net)} paid to ${staffEntry.user_name}`,
      );
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to process payment");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      label: "Base Salary",
      value: staffEntry.base_salary,
      color: "text-white",
    },
    {
      label: `Commission (${staffEntry.commission_count} entries)`,
      value: staffEntry.commission_total,
      color: "text-purple-400",
    },
    ...(parseFloat(bonus) > 0
      ? [
          {
            label: "Bonus",
            value: parseFloat(bonus),
            color: "text-emerald-400",
          },
        ]
      : []),
    ...(parseFloat(deductions) > 0
      ? [
          {
            label: "Deductions",
            value: -parseFloat(deductions),
            color: "text-rose-400",
          },
        ]
      : []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/15 rounded-xl border border-emerald-500/20">
              <IndianRupee size={17} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                Process Salary
              </p>
              <p className="text-white font-black text-sm">
                {staffEntry.user_name}
              </p>
              <p className="text-stone-500 text-[10px]">{monthLabel(month)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Breakdown */}
          <div className="bg-white/5 border border-white/8 rounded-2xl overflow-hidden">
            <p className="text-[10px] text-stone-500 uppercase tracking-widest px-4 pt-3 pb-2 font-bold border-b border-white/8">
              Pay Breakdown
            </p>
            <div className="px-4 py-3 space-y-2">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-stone-400">{r.label}</span>
                  <span className={`font-mono font-bold ${r.color}`}>
                    {r.value < 0 ? "−" : ""}₹{fmtMoney(Math.abs(r.value))}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between text-base font-black border-t border-white/10 pt-2 mt-1">
                <span className="text-white">Net Payable</span>
                <span className="font-mono text-emerald-400">
                  ₹{fmtMoney(net)}
                </span>
              </div>
            </div>
          </div>

          {/* Bonus & Deductions */}
          <div className="grid grid-cols-2 gap-3">
            {[
              {
                label: "Bonus (₹)",
                key: "bonus",
                val: bonus,
                set: setBonus,
                color: "focus:ring-emerald-500/40 text-emerald-300",
              },
              {
                label: "Deductions (₹)",
                key: "deductions",
                val: deductions,
                set: setDeductions,
                color: "focus:ring-rose-500/40 text-rose-300",
              },
            ].map((f) => (
              <div key={f.key}>
                <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
                  {f.label}
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={f.val}
                    onChange={(e) => f.set(e.target.value)}
                    className={`w-full h-10 bg-white/5 border border-white/10 rounded-xl pl-7 pr-3 text-sm font-mono outline-none focus:ring-2 ${f.color} placeholder-stone-700`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Payment mode */}
          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-2 font-bold">
              Payment Mode
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_MODES.map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`py-2.5 px-3 rounded-xl border text-xs font-bold text-left transition-all capitalize ${
                    mode === m
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                      : "bg-white/5 border-white/10 text-stone-500 hover:border-white/20"
                  }`}
                >
                  {m.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
              Remarks
            </p>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Paid via NEFT, Ref: 12345"
              className="w-full h-10 bg-white/5 border border-white/10 rounded-xl px-4 text-stone-300 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40 placeholder-stone-600"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handlePay}
            disabled={saving || net <= 0}
            className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <CheckCircle2 size={15} />
            )}
            Pay ₹{fmtMoney(net)}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Commission Detail Drawer ──────────────────────────────────
const CommissionDrawer = ({ staffEntry, onClose }) => {
  if (!staffEntry) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-end p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 60, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-sm max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-white font-black">{staffEntry.user_name}</p>
            <p className="text-stone-500 text-[11px]">Commission breakdown</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {staffEntry.commissions?.length === 0 ? (
            <div className="text-center py-12 text-stone-600 text-sm">
              No commission entries this month
            </div>
          ) : (
            <div className="space-y-2.5">
              {staffEntry.commissions?.map((c, i) => (
                <motion.div
                  key={c.id || i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white/5 border border-white/8 rounded-2xl px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {c.notes && (
                        <p className="text-stone-300 text-xs font-semibold mb-1 truncate">
                          "{c.notes}"
                        </p>
                      )}
                      <p className="text-stone-500 text-[10px]">
                        {fmt(c.date)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono font-black text-sm text-white">
                        ₹{fmtMoney(c.amount)}
                      </p>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}
                      >
                        {c.status}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-stone-400 text-sm">Total</span>
            <span className="font-mono font-black text-lg text-purple-400">
              ₹{fmtMoney(staffEntry.commission_total)}
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Staff Salary Card ─────────────────────────────────────────
const StaffSalaryCard = ({
  entry,
  index,
  isAdmin,
  month,
  onSetSalary,
  onPay,
  onViewCommissions,
  onReverse,
  token,
}) => {
  const isPaid = entry.status === "paid";
  const hasNoSalary = entry.net_payable === 0 && entry.commission_total === 0;
  const meta = roleMeta(entry.user_role);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`relative bg-white border rounded-2xl overflow-hidden transition-all hover:shadow-lg group ${
        isPaid
          ? "border-emerald-200/60 bg-gradient-to-br from-white to-emerald-50/30"
          : hasNoSalary
            ? "border-stone-200/60 opacity-60"
            : "border-stone-100 hover:-translate-y-0.5"
      }`}
    >
      {/* Paid stamp */}
      {isPaid && (
        <div className="absolute top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg shadow-emerald-900/20">
            <BadgeCheck size={10} /> PAID
          </span>
        </div>
      )}

      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-3.5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-black text-base">
              {entry.user_name?.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-stone-800 text-sm truncate">
              {entry.user_name}
            </p>
            <p className="text-stone-400 text-[10px] truncate">
              {entry.user_email}
            </p>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border mt-1 ${meta.color}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {entry.user_role}
            </span>
          </div>
          {isPaid && entry.payment && (
            <div className="text-right text-[10px] text-stone-400 shrink-0">
              <p>{fmt(entry.payment.paid_at)}</p>
              <p className="capitalize">
                {entry.payment.payment_mode?.replace("_", " ")}
              </p>
            </div>
          )}
        </div>

        {/* Pay breakdown */}
        <div className="grid grid-cols-3 gap-2 mb-3.5">
          <div className="bg-stone-50 rounded-xl px-3 py-2.5 text-center">
            <p className="text-[9px] text-stone-400 uppercase font-bold mb-0.5">
              Base
            </p>
            <p className="font-mono font-black text-stone-700 text-sm">
              ₹{fmtMoney(entry.base_salary)}
            </p>
            {isAdmin && (
              <button
                onClick={() => onSetSalary(entry)}
                className="text-[9px] text-amber-500 hover:text-amber-600 mt-0.5 flex items-center gap-0.5 mx-auto transition-colors"
              >
                <Pencil size={8} /> edit
              </button>
            )}
          </div>
          <button
            onClick={() =>
              entry.commission_count > 0 && onViewCommissions(entry)
            }
            className={`rounded-xl px-3 py-2.5 text-center transition-all ${
              entry.commission_count > 0
                ? "bg-purple-50 hover:bg-purple-100 cursor-pointer"
                : "bg-stone-50 cursor-default"
            }`}
          >
            <p className="text-[9px] text-stone-400 uppercase font-bold mb-0.5">
              Commission
            </p>
            <p
              className={`font-mono font-black text-sm ${entry.commission_total > 0 ? "text-purple-700" : "text-stone-400"}`}
            >
              ₹{fmtMoney(entry.commission_total)}
            </p>
            {entry.commission_count > 0 && (
              <p className="text-[9px] text-purple-500 mt-0.5">
                {entry.commission_count} entries
              </p>
            )}
          </button>
          <div
            className={`rounded-xl px-3 py-2.5 text-center ${entry.net_payable > 0 ? "bg-amber-50" : "bg-stone-50"}`}
          >
            <p className="text-[9px] text-stone-400 uppercase font-bold mb-0.5">
              Net Pay
            </p>
            <p
              className={`font-mono font-black text-sm ${entry.net_payable > 0 ? "text-amber-700" : "text-stone-400"}`}
            >
              ₹{fmtMoney(entry.net_payable)}
            </p>
            {(entry.deductions > 0 || entry.bonus > 0) && (
              <p className="text-[9px] text-stone-400 mt-0.5">
                {entry.bonus > 0 ? `+₹${fmtMoney(entry.bonus)} bonus` : ""}
                {entry.deductions > 0 ? ` −₹${fmtMoney(entry.deductions)}` : ""}
              </p>
            )}
          </div>
        </div>

        {/* Pending commission warning */}
        {entry.commission_pending > 0 && !isPaid && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200/60 rounded-xl px-3 py-2 mb-3 text-[11px]">
            <AlertCircle size={11} className="text-amber-500 shrink-0" />
            <span className="text-amber-700">
              ₹{fmtMoney(entry.commission_pending)} commission still pending
              payout
            </span>
          </div>
        )}

        {/* Paid info */}
        {isPaid && entry.payment?.notes && (
          <p className="text-[11px] text-stone-400 italic mb-3">
            "{entry.payment.notes}"
          </p>
        )}

        {/* Actions */}
        {isAdmin && (
          <div className="flex gap-2">
            {!isPaid && entry.net_payable > 0 && (
              <button
                onClick={() => onPay(entry)}
                className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-black transition-all shadow-md shadow-emerald-900/15"
              >
                <IndianRupee size={11} /> Pay Salary
              </button>
            )}
            {!isPaid &&
              entry.base_salary === 0 &&
              entry.commission_total === 0 && (
                <button
                  onClick={() => onSetSalary(entry)}
                  className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 text-amber-600 border border-amber-300/40 text-[11px] font-bold transition-all"
                >
                  <Plus size={11} /> Set Salary
                </button>
              )}
            {isPaid && entry.payment && (
              <button
                onClick={() => onReverse(entry.payment.id)}
                className="flex items-center justify-center gap-1.5 h-9 px-4 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200/60 text-[11px] font-bold transition-all opacity-0 group-hover:opacity-100"
              >
                <Trash2 size={11} /> Reverse
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ── My Salary View (non-admin) ────────────────────────────────
const MySalaryView = ({ token, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/salaries/my`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load salary data"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={24} className="animate-spin text-stone-400" />
      </div>
    );

  return (
    <div className="space-y-5 max-w-lg mx-auto">
      {/* Config card */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-2xl p-5 shadow-xl"
      >
        <p className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-4">
          Your Salary Package
        </p>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-900/30">
            <span className="text-white font-black text-xl">
              {user?.name?.charAt(0)}
            </span>
          </div>
          <div>
            <p className="text-white font-black text-lg">{user?.name}</p>
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border ${roleMeta(user?.role).color}`}
            >
              {user?.role}
            </span>
          </div>
          <div className="ml-auto text-right">
            <p className="text-stone-500 text-[10px] uppercase font-bold">
              Base Salary
            </p>
            <p className="font-mono font-black text-2xl text-amber-400">
              ₹{fmtMoney(data?.config?.base_salary || 0)}
            </p>
            <p className="text-stone-500 text-[10px]">per month</p>
          </div>
        </div>
      </motion.div>

      {/* Payment history */}
      {data?.payments?.length > 0 ? (
        <div className="space-y-3">
          <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
            Payment History
          </p>
          {data.payments.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className={`bg-white border rounded-2xl p-4 ${p.status === "paid" ? "border-emerald-200/60" : "border-stone-100"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-stone-800">
                    {monthLabel(p.month)}
                  </p>
                  <p className="text-stone-400 text-[11px] mt-0.5">
                    Base ₹{fmtMoney(p.base_salary)} + Commission ₹
                    {fmtMoney(p.commission_total)}
                    {p.bonus > 0 && ` + Bonus ₹${fmtMoney(p.bonus)}`}
                    {p.deductions > 0 &&
                      ` − Deductions ₹${fmtMoney(p.deductions)}`}
                  </p>
                  {p.notes && (
                    <p className="text-stone-400 text-[10px] italic mt-0.5">
                      "{p.notes}"
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-mono font-black text-lg text-stone-800">
                    ₹{fmtMoney(p.net_payable)}
                  </p>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  >
                    {p.status === "paid" ? `Paid ${fmt(p.paid_at)}` : "Pending"}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-stone-400">
          <Wallet size={32} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No salary records yet</p>
        </div>
      )}
    </div>
  );
};

// ── Main Salaries Page ────────────────────────────────────────
const Salaries = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canManage = isAdmin || isManager;

  const [month, setMonth] = useState(currentMonthStr());
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setSalaryTarget, setSetSalaryTarget] = useState(null); // staff entry
  const [payTarget, setPayTarget] = useState(null); // staff entry to pay
  const [commDrawer, setCommDrawer] = useState(null); // staff entry for drawer
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPayroll = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/salaries/payroll?month=${month}`,
        { headers },
      );
      setPayroll(data);
    } catch {
      toast.error("Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  }, [month, token]);

  useEffect(() => {
    fetchPayroll();
  }, [month]);

  const handleSetSalary = async (data) => {
    try {
      await axios.post(`${API_URL}/salaries/configs`, data, { headers });
      toast.success("Base salary updated!");
      fetchPayroll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update salary");
      throw err;
    }
  };

  const handlePaid = () => fetchPayroll();

  const handleReverse = async (paymentId) => {
    if (!window.confirm("Reverse this salary payment? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API_URL}/salaries/payment/${paymentId}`, {
        headers,
      });
      toast.success("Payment reversed");
      fetchPayroll();
    } catch {
      toast.error("Failed to reverse payment");
    }
  };

  const filtered = useMemo(() => {
    if (!payroll?.staff) return [];
    return payroll.staff.filter((e) => {
      if (filterStatus === "paid" && e.status !== "paid") return false;
      if (
        filterStatus === "pending" &&
        (e.status !== "pending" || e.net_payable === 0)
      )
        return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !e.user_name?.toLowerCase().includes(q) &&
          !e.user_email?.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [payroll, filterStatus, search]);

  const summary = payroll?.summary || {};

  if (!canManage) {
    return (
      <div className="min-h-full bg-[#f7f6f3] p-4 md:p-8 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden bg-[#1c1c2e] rounded-2xl p-6 mb-6 shadow-xl"
        >
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 80% 50%, #f59e0b 0%, transparent 50%)",
            }}
          />
          <div className="relative flex items-center gap-4">
            <div className="p-3 bg-amber-500/15 rounded-2xl border border-amber-500/20">
              <Wallet size={24} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white">My Salary</h1>
              <p className="text-stone-400 text-sm mt-0.5">
                Your monthly compensation details
              </p>
            </div>
          </div>
        </motion.div>
        <MySalaryView token={token} user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f7f6f3] p-4 md:p-6 lg:p-8">
      {/* ── Hero ── */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-[#1c1c2e] rounded-2xl p-6 mb-6 shadow-xl"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 40%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 10% 70%, #10b981 0%, transparent 45%)",
          }}
        />

        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/15 rounded-2xl border border-amber-500/20">
              <Wallet size={24} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                Payroll Management
              </h1>
              <p className="text-stone-400 text-sm mt-0.5">
                Base salaries + commissions in one place
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {payroll && (
          <div className="relative mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              {
                label: "Total Payroll",
                value: `₹${fmtMoney(summary.total_net_payable)}`,
                color: "text-amber-400",
              },
              {
                label: "Base Salaries",
                value: `₹${fmtMoney(summary.total_base)}`,
                color: "text-stone-300",
              },
              {
                label: "Commissions",
                value: `₹${fmtMoney(summary.total_commission)}`,
                color: "text-purple-400",
              },
              {
                label: "Paid / Pending",
                value: `${summary.paid_count} / ${summary.pending_count}`,
                color:
                  summary.pending_count > 0
                    ? "text-rose-400"
                    : "text-emerald-400",
              },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-stone-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                  {s.label}
                </p>
                <p className={`font-mono font-black text-xl ${s.color}`}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* ── Month Navigator ── */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-[#1c1c2e] border border-white/10 rounded-xl px-1 py-1 shadow">
          <button
            onClick={() => setMonth(prevMonth(month))}
            className="p-1.5 rounded-lg hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs font-bold text-stone-300 min-w-32 text-center">
            {monthLabel(month)}
          </span>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonthStr()}
            className="p-1.5 rounded-lg hover:bg-white/10 text-stone-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Pending alert */}
        {summary.pending_count > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm">
            <AlertCircle size={14} className="text-amber-500" />
            <span className="text-amber-800 font-semibold">
              {summary.pending_count} staff pending — ₹
              {fmtMoney(
                (payroll?.staff || [])
                  .filter((e) => e.status === "pending" && e.net_payable > 0)
                  .reduce((s, e) => s + e.net_payable, 0),
              )}{" "}
              to disburse
            </span>
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3.5 mb-5 shadow-sm flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-36">
          <User
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff…"
            className="w-full h-9 pl-8 pr-3 text-xs bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/30 text-stone-700"
          />
        </div>
        {[
          { val: "all", label: "All Staff" },
          { val: "pending", label: "Pending" },
          { val: "paid", label: "Paid" },
        ].map((f) => (
          <button
            key={f.val}
            onClick={() => setFilterStatus(f.val)}
            className={`h-9 px-4 rounded-xl text-xs font-bold transition-all border ${
              filterStatus === f.val
                ? "bg-[#1c1c2e] text-white border-transparent"
                : "bg-white text-stone-500 border-stone-200 hover:border-stone-300"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-stone-400 font-semibold">
          {filtered.length} staff
        </span>
      </div>

      {/* ── Grid ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="relative w-14 h-14 mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-amber-100" />
            <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-stone-400 text-sm">Loading payroll…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-stone-100">
          <div className="p-4 bg-stone-100 rounded-2xl mb-3">
            <Users size={28} className="text-stone-300" />
          </div>
          <p className="text-stone-500 font-semibold text-sm">
            No records found
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {!payroll ? "Failed to load" : "Try changing the filter or month"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((entry, i) => (
              <StaffSalaryCard
                key={entry.user_id}
                entry={entry}
                index={i}
                isAdmin={isAdmin}
                month={month}
                token={token}
                onSetSalary={setSetSalaryTarget}
                onPay={setPayTarget}
                onViewCommissions={setCommDrawer}
                onReverse={handleReverse}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* ── Monthly summary footer ── */}
      {payroll && filtered.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 bg-[#1c1c2e] border border-white/10 rounded-2xl p-5 shadow-xl"
        >
          <p className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <BarChart3 size={11} className="text-amber-400" /> Payroll Summary —{" "}
            {monthLabel(month)}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              {
                label: "Base Salaries",
                value: summary.total_base,
                color: "text-stone-300",
              },
              {
                label: "Commissions",
                value: summary.total_commission,
                color: "text-purple-400",
              },
              {
                label: "Bonuses",
                value: summary.total_bonus,
                color: "text-emerald-400",
              },
              {
                label: "Deductions",
                value: summary.total_deductions,
                color: "text-rose-400",
              },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 rounded-xl px-4 py-3">
                <p className="text-[10px] text-stone-500 uppercase font-bold mb-1">
                  {s.label}
                </p>
                <p className={`font-mono font-black text-lg ${s.color}`}>
                  ₹{fmtMoney(s.value)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-white font-black text-base">
              Total Net Payable
            </span>
            <span className="font-mono font-black text-2xl text-amber-400">
              ₹{fmtMoney(summary.total_net_payable)}
            </span>
          </div>
        </motion.div>
      )}

      {/* ── Modals / Drawers ── */}
      <AnimatePresence>
        {setSalaryTarget && (
          <SetSalaryModal
            staff={setSalaryTarget}
            existingSalary={setSalaryTarget.base_salary}
            onClose={() => setSetSalaryTarget(null)}
            onSave={handleSetSalary}
          />
        )}
        {payTarget && (
          <PaySalaryModal
            staffEntry={payTarget}
            month={month}
            token={token}
            onClose={() => setPayTarget(null)}
            onPaid={handlePaid}
          />
        )}
        {commDrawer && (
          <CommissionDrawer
            staffEntry={commDrawer}
            onClose={() => setCommDrawer(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Salaries;
